<template>
    <pc-loader v-bind:loading="loadingItems">
        <div>
            <div class="w-100 container my-2">
                <div class="row">
                    <div
                        v-if="!hideSearch"
                        class="my-3 col"
                        data-gtmid="categorized-filter-search">
                        <pc-search
                            v-bind:dynamic="dynamicSearch"
                            v-on:search="search" />
                    </div>
                </div>
                <div class="row">
                    <div
                        v-if="internalCategories.length > 0"
                        class="col-12 col-md-5">
                        <div class="d-flex justify-content-between">
                            <div>
                                <h4>{{ filtersHeader }}</h4>
                            </div>
                            <pc-button
                                class="align-self-end"
                                link
                                v-on:click="resetFilters">
                                {{ resetFilterButtonLabel }}
                            </pc-button>
                        </div>
                        <div
                            v-for="(category, index) in internalCategories"
                            v-bind:key="getCategoryKey(category)"
                            class="me-1">
                            <hr v-if="index !== 0" />
                            <pc-section
                                class="my-3"
                                v-bind:title="category.label">
                                <template v-slot:title>
                                    <div class="d-flex flex-row w-100">
                                        <div class="fw-bold">
                                            {{ category.label }}
                                        </div>
                                        <div>
                                            <div
                                                v-if="showFilterCount(category)"
                                                class="ms-2 px-2 my-1 align-items-center bg-primary-200 d-inline-flex fw-bold justify-content-center rounded-pill small">
                                                {{ activeFilterCount(category) }}
                                            </div>
                                        </div>
                                    </div>
                                </template>
                                <div class="my-2">
                                    <pc-button
                                        v-if="showCategoryAllOption(category)"
                                        class="p-0"
                                        link
                                        v-bind:data-gtmid="'categorized-filter-' + category.label + '-select-all'"
                                        v-on:click="updateCategoryAllState(category)">
                                        {{ getCategoryAllButtonDisplay(category) }}
                                    </pc-button>
                                    <pc-checkbox
                                        v-for="option in category.options"
                                        v-bind:key="getCategoryOptionKey(category, option)"
                                        v-bind:model-value="option.selected"
                                        v-bind:label="option.label"
                                        v-bind:data-gtmid="'categorized-filter' + category.label + '-' + option.label"
                                        v-bind:disabled="!getCategoryOptionEnabled(option)"
                                        v-on:update:model-value="
                                            updateCategoryOptionSelected(category, option, $event)
                                        " />
                                </div>
                            </pc-section>
                        </div>
                    </div>
                    <div
                        ref="itemsWrapper"
                        class="col position-relative">
                        <!-- placing the clone at the top of the column makes the math a bit easier to read -->
                        <categorized-item
                            v-if="showClone"
                            v-bind:drag-only="dragOnly"
                            dragged
                            v-bind:item="cloneItem"
                            v-bind:style="cloneStyle">
                            <slot v-bind:item="cloneItem" />
                        </categorized-item>
                        <div>
                            <h4>{{ itemsHeader }}</h4>
                        </div>
                        <div v-if="filteredItems.length > 0">
                            <div
                                v-if="!dragOnly"
                                class="mt-1">
                                <pc-button
                                    v-if="!readonly"
                                    small
                                    class="me-1 mb-2"
                                    data-gtmid="categorized-filter-select-all"
                                    v-on:click="selectAllVisible(true)">
                                    {{ selectAllButtonLabel }}
                                </pc-button>
                                <pc-button
                                    v-if="!readonly"
                                    small
                                    class="me-1 mb-2"
                                    data-gtmid="categorized-filter-select-none"
                                    v-on:click="selectAllVisible(false)">
                                    {{ selectNoneButtonLabel }}
                                </pc-button>
                            </div>
                            <div
                                ref="itemsScrollContainer"
                                v-bind:style="itemsStyle"
                                v-on:scroll="onFilteredItemsScroll">
                                <pc-interact
                                    v-bind:is-draggable="draggable"
                                    v-on:initial-move="initialMove"
                                    v-on:drag-move="dragMove"
                                    v-on:drag-end="dragEnd">
                                    <div class="bg-light me-1">
                                        <component
                                            v-bind:is="listComponent"
                                            name="list"
                                            tag="div">
                                            <div
                                                v-for="item in filteredDisplayItems"
                                                v-bind:key="item.key"
                                                class="list-item">
                                                <categorized-item
                                                    ref="filteredDisplayItems"
                                                    v-bind:item="item"
                                                    v-bind:selected="getItemSelected(item.key)"
                                                    v-bind:collapsed="isCollapsed(item.key)"
                                                    v-bind:grouper-collapsed="isGrouperCollapsed(item.grouperKey)"
                                                    v-bind:drag-only="dragOnly"
                                                    v-bind:disabled="disableSelect"
                                                    v-bind:readonly="readonly"
                                                    v-on:item-selected="selectItem(item.key, $event)"
                                                    v-on:toggle-grouper="toggleCollapsed(item.key)">
                                                    <slot v-bind:item="item" />
                                                </categorized-item>
                                            </div>
                                        </component>
                                    </div>
                                </pc-interact>
                                <!-- spinning load indicator for large lists -->
                                <div
                                    v-if="moreFilteredItemsToDisplay"
                                    class="d-flex justify-content-center my-1">
                                    <div
                                        class="spinner-border spinner-border-sm text-primary"
                                        role="status">
                                        <span class="visually-hidden">{{ loadingIndicatorSRText }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div v-else>
                            <div>
                                <em>{{ emptyText }}</em>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </pc-loader>
</template>

<script>
    import categorizedItem from "./_categorized-filter-item.vue";
    import { HUB_PUBLISH_MIXIN, HUB_ACCEPT_EXTERNAL_MIXIN } from "Components/orderable/_util.js";
    import { escapeRegex, simpleDebounce } from "Util/util";
    import { mapI18n } from "Util/i18n";
    import { KEYS, NAMESPACE } from "./i18n/pcCategorizedFilter.i18n";

    const DYNAMIC_DEFAULT = { debounce: 250, reactive: true };
    const FILTERED_ITEMS_CHUNK_SIZE = 300;

    function indeterminateStateFromNumeric(selected, available) {
        return available === selected ? true : selected === 0 ? false : null;
    }

    function isNearBottom(element) {
        return element.clientHeight + element.scrollTop >= element.scrollHeight - 10;
    }

    /**
     * Validates category property in non-production environments.
     * The expected structure is:
     *   {
     *     label - Label for category.
     *     function - Function to test item for inclusion, given category filters. Required if no property.
     *     property - Property on item to test against filters for category. Required if no function.
     *     options:
     *     {
     *       label - Label for option.
     *       value - Value to test against for filter.
     *       initiallySelected - Initialize as selected. Default is all options unselected.
     *       enabled - If false, options will appear unselected and uninteractable.
     *       childCategory - Use sparingly. Treats an entire category as subfilters on this option.
     *     }
     *   }
     */
    function validateCategoryStructure(categories) {
        for (const category of categories) {
            if (!category.hasOwnProperty("label")) {
                console.error("Incorrect format for 'category' prop. " + "Property 'label' not found on category.");
                return false;
            } else if (!category.hasOwnProperty("function") && !category.hasOwnProperty("property")) {
                console.error(
                    "Incorrect format for 'category' prop. " +
                        `One of the following properties was expected on category '${category.label}': ['function', 'property'] `
                );
                return false;
            } else if (!category.hasOwnProperty("options")) {
                console.error(
                    "Incorrect format for 'category' prop. " +
                        `Property 'options' not found on category '${category.label}'`
                );
                return false;
            }
            for (const option of category.options) {
                if (!option.hasOwnProperty("label")) {
                    console.error(
                        "Incorrect format for 'category' prop. " +
                            `Property 'label' not found on category '${category.label}' option.`
                    );
                    return false;
                } else if (!option.hasOwnProperty("value")) {
                    console.error(
                        "Incorrect format for 'category' prop. " +
                            `Property 'value' not found on category option '${category.label}-${option.label}'.`
                    );
                    return false;
                }
            }
        }
        return true;
    }

    export default {
        components: { categorizedItem },
        mixins: [HUB_PUBLISH_MIXIN, HUB_ACCEPT_EXTERNAL_MIXIN],
        props: {
            items: { type: [Array, Function], required: true },
            selected: { type: Array, required: true },

            categories: { type: Array, required: false, default: () => [], validator: validateCategoryStructure },

            orderableHub: { type: Object, required: false, default: null },
            // used to fix the width of the placeholder to the size of the orderable.
            // default is the width of the dragged item
            placeholderWidth: { type: Number, required: false, default: null },
            dragOnly: { type: Boolean, required: false, default: false },
            hideSearch: { type: Boolean, required: false, default: false },
            dynamicSearch: { type: Object, required: false, default: () => DYNAMIC_DEFAULT },
            keyProp: { type: String, required: false, default: "key" },
            labelProp: { type: String, required: false, default: "label" },
            // function or property that indicates the key of the item another item should be grouped under
            itemGrouper: { type: [String, Function], required: false, default: null },
            emptyTextOverride: { type: String, required: false, default: "" },
            headerOverride: { type: String, required: false, default: "" },
            // dictates max height; around how many items should fit into the list area at once
            visibleItems: { type: Number, required: false, default: 10 },
            disableSelect: { type: Boolean, required: false, default: false },
            readonly: { type: Boolean, required: false, default: false }
        },
        emits: ["filter-reset", "filter-selected", "items-selected", "drag-start", "drag-move", "drag-end"],
        data() {
            const initialCategoryState = this._buildCategoryState({}, true);
            return {
                filteredItems: [],
                filteredDisplayItems: [],
                collapsedGroupers: [],
                internalCategoryState: initialCategoryState,
                internalGrouperMap: {},
                searchTerm: "",
                loadingItems: false,
                loadingGroupers: false,
                loadingFilteredItemsChunk: false,
                currentDisplayItemIndex: 0,
                includedKeys: null,
                dragItemKey: null, // named as required by orderable hub mixin
                draggingStartRect: null, // named as required by orderable hub mixin
                orderable: null, // named as required by orderable hub mixin
                dragState: {
                    key: null,
                    event: null,
                    moving: false,
                    rect: null,
                    parentRect: null,
                    left0: 0,
                    top0: 0,
                    x: 0,
                    y: 0
                }
            };
        },
        computed: {
            listComponent() {
                return this.filteredDisplayItems.length > 3000 ? "div" : "transitionGroup";
            },
            query() {
                return {
                    Search: {
                        Value: this.searchTerm,
                        IsRegex: false
                    },
                    CategorizedFilters: this.activeFilters
                };
            },
            queryTrigger() {
                // Watching this prop instead of query can prevent duplicate _performQuery calls
                // if the structure of the query has not actually changed.
                return JSON.stringify(this.query);
            },
            internalCategories() {
                const categories = [];
                this.categories.forEach((category) => {
                    const categoryKey = this.getCategoryKey(category);
                    if (!this.internalCategoryState.hasOwnProperty(categoryKey)) {
                        return;
                    }
                    if (category.hidden) {
                        return;
                    } // do not display hidden categories or include in query, but keep state

                    const categoryClone = { ...category };
                    const options = [];
                    let selectedOptionCount = 0;
                    for (const option of category.options) {
                        let selected = false;
                        const optionKey = this.getCategoryOptionKey(category, option);
                        if (this.internalCategoryState[categoryKey].hasOwnProperty(optionKey)) {
                            selected = this.internalCategoryState[categoryKey][optionKey];
                            if (selected) {
                                selectedOptionCount++;
                            }
                        }
                        options.push({ ...option, selected: selected });
                    }
                    categoryClone.options = options;
                    categoryClone.selectedOptionCount = selectedOptionCount;
                    categories.push(categoryClone);
                });
                return categories;
            },
            activeFilters() {
                const filters = [];
                this.internalCategories.forEach((category) => {
                    if (!category.options || category.options.length < 1) {
                        return;
                    }

                    const filterValues = category.options
                        .filter((option) => option.selected && this.getCategoryOptionEnabled(option))
                        .map((option) => option.value);

                    const selectableOptionCount = category.options.filter((option) =>
                        this.getCategoryOptionEnabled(option)
                    ).length;

                    // if every selectable option is selected, exclude from filtering logic for performance
                    // if no options are selected, exclude from filtering logic for performance
                    if (
                        !category.alwaysPassFilters &&
                        (filterValues.length === selectableOptionCount || filterValues.length === 0)
                    ) {
                        return;
                    }

                    filters.push({
                        Key: this.getCategoryKey(category),
                        Values: filterValues
                    });
                });
                return filters;
            },
            relatedCategoriesMap() {
                const relatedCategories = {};

                /**
                 * If using related categories, create a map of relationships between
                 * category options and category groups. If an option in the child category
                 * is selected, the parent option will be selected. If the parent is unselected,
                 * so will all options in the group.
                 */
                this.internalCategories.forEach((category) => {
                    const categoryKey = this.getCategoryKey(category);
                    for (const option of category.options) {
                        if (option.childCategory) {
                            if (!relatedCategories.hasOwnProperty("options")) {
                                // Initialize related category maps when first configured option is found. This
                                // keeps the object empty unless data exists in the maps.
                                relatedCategories.options = {};
                                relatedCategories.categories = {};
                            }

                            const optionKey = this.getCategoryOptionKey(category, option);
                            relatedCategories.options[optionKey] = option.childCategory;
                            relatedCategories.categories[option.childCategory] = {
                                category: categoryKey,
                                option: optionKey,
                                optionValue: option.value
                            };
                        }
                    }
                });
                return relatedCategories;
            },
            selectedItemSet() {
                return new Set(this.selected);
            },
            searchMatcher() {
                return this.searchTerm ? new RegExp(escapeRegex(this.searchTerm), "i") : null;
            },
            itemsHeader() {
                return this.headerOverride || this.defaultItemsHeader;
            },
            itemsStyle() {
                return {
                    "max-height": `${Math.max(150, 50 * this.visibleItems + 50)}px`,
                    "overflow-y": "auto",
                    "overflow-x": "hidden"
                };
            },
            emptyText() {
                return this.emptyTextOverride || this.defaultEmptyItemsText;
            },
            showClone() {
                return this.dragState.moving && this.cloneItem;
            },
            cloneItem() {
                return this.filteredDisplayItems.find((item) => item.key === this.dragState.key);
            },
            cloneHeight() {
                return this.dragState.rect ? this.dragState.rect.height : 0;
            },
            cloneWidth() {
                return this.dragState.rect ? this.dragState.rect.width : 0;
            },
            cloneStyle() {
                if (!this.dragState.moving) {
                    return null;
                }
                return {
                    left: `${this.dragState.x}px`,
                    top: `${this.dragState.y}px`,
                    opacity: ".8",
                    transitionProperty: "none",
                    transitionDuration: "0s",
                    position: "absolute",
                    height: `${this.cloneHeight}px`,
                    width: `${this.cloneWidth}px`,
                    "z-index": "10"
                };
            },
            moreFilteredItemsToDisplay() {
                return this.currentDisplayItemIndex < this.filteredItems.length;
            },
            filteredItemSet() {
                return new Set(this.filteredItems.map((item) => this.getItemKey(item)));
            },
            draggable() {
                return !this.disableSelect & !this.readonly;
            },
            ...mapI18n(KEYS, NAMESPACE)
        },
        watch: {
            queryTrigger() {
                this._performQuery();
            },
            items: {
                handler() {
                    this._buildInternalGrouperMap();
                    this._performQuery();
                },
                deep: true
            },
            categories: {
                handler() {
                    this.internalCategoryState = this._buildCategoryState(this.internalCategoryState, false);
                },
                deep: true
            }
        },
        async created() {
            await this._buildInternalGrouperMap();
            await this._performQuery();
        },
        methods: {
            search(searchTerm) {
                this.searchTerm = searchTerm;
            },
            resetFilters() {
                this.$emit("filter-reset");
                this.internalCategoryState = this._buildCategoryState({}, true);
            },
            getItemLabel(item) {
                return item[this.labelProp];
            },
            getItemKey(item) {
                return item[this.keyProp];
            },
            getItemGrouper(item) {
                if (!this.itemGrouper) {
                    return null;
                } else if (typeof this.itemGrouper === "function") {
                    return this.itemGrouper(item);
                } else {
                    return item[this.itemGrouper] || null;
                }
            },
            getItemSelected(itemKey) {
                return this.isItemGrouper(itemKey)
                    ? this.getGrouperSelected(itemKey)
                    : this.selectedItemSet.has(itemKey);
            },
            getGrouperSelected(itemKey) {
                const groupedItemCount = this.internalGrouperMap[itemKey].length;
                const selectedItemCount = this.internalGrouperMap[itemKey].filter((key) =>
                    this.selectedItemSet.has(key)
                ).length;
                return indeterminateStateFromNumeric(selectedItemCount, groupedItemCount);
            },
            isItemGrouper(itemKey) {
                return this.internalGrouperMap.hasOwnProperty(itemKey);
            },
            isCollapsed(itemKey) {
                return this.collapsedGroupers.includes(itemKey);
            },
            isGrouperCollapsed(grouperKey) {
                return grouperKey && this.collapsedGroupers.includes(grouperKey);
            },
            getCategoryKey(category) {
                // key is optional, but should be provided if using a query for items
                return category.key ?? `category-${category.label}`;
            },
            getCategoryOptionKey(category, option) {
                return `category-${category.label}-option-${option.label}`;
            },
            getCategoryOptionEnabled(option) {
                return option.enabled ?? true;
            },
            showCategoryAllOption(category) {
                return !category.disableAll && category.options && category.options.length > 0;
            },
            showFilterCount(category) {
                return category.selectedOptionCount > 0;
            },
            activeFilterCount(category) {
                return category.selectedOptionCount;
            },
            updateCategoryOptionSelected(category, option, selected) {
                const categoryKey = this.getCategoryKey(category);
                const optionKey = this.getCategoryOptionKey(category, option);
                this.internalCategoryState[categoryKey][optionKey] = selected;
                this.$emit("filter-selected", { key: categoryKey, value: option.value, selected });

                this._updateRelatedCategoriesFromSingle(category, option, selected);
            },
            getCategoryAllButtonDisplay(category) {
                return this.getCategoryAllState(category)
                    ? this.selectNoneFiltersButtonLabel
                    : this.selectAllFiltersButtonLabel;
            },
            getCategoryAllState(category) {
                const categoryKey = this.getCategoryKey(category);
                const availableFilterCount = category.options
                    ? category.options.filter((option) => this.getCategoryOptionEnabled(option)).length
                    : 0;
                const selectedFilterCount = Object.values(this.internalCategoryState[categoryKey]).filter(
                    (selected) => selected
                ).length;
                return indeterminateStateFromNumeric(selectedFilterCount, availableFilterCount);
            },
            updateCategoryAllState(category) {
                const categoryKey = this.getCategoryKey(category);
                const selected = !this.getCategoryAllState(category);
                if (category.options) {
                    category.options.forEach((option) => {
                        const optionKey = this.getCategoryOptionKey(category, option);
                        if (this.getCategoryOptionEnabled(option)) {
                            this.internalCategoryState[categoryKey][optionKey] = selected;
                            this.$emit("filter-selected", { key: categoryKey, value: option.value, selected });
                        }
                    });
                }

                this._updateRelatedCategoriesFromAll(category, selected);
            },
            async selectItem(itemKey, { selected, itemComponent }) {
                let keys;
                // if grouped, toggle children
                if (this.isItemGrouper(itemKey)) {
                    keys = [];

                    for (const k of this.internalGrouperMap[itemKey]) {
                        if (this.filteredItemSet.has(k)) {
                            keys.push(k);
                        }
                    }
                } else {
                    keys = [itemKey];
                }

                this.$emit("items-selected", { keys, selected, itemComponent });
            },
            reRender() {
                for (let index = 0; index < this.$refs.filteredDisplayItems.length; index++) {
                    const displayItem = this.$refs.filteredDisplayItems[index];
                    displayItem.reRender();
                }
            },
            async selectAllVisible(selected) {
                const keys = this.filteredItems
                    .map((item) => this.getItemKey(item))
                    .filter((itemKey) => !this.isItemGrouper(itemKey));

                this.$emit("items-selected", { keys, selected });
            },
            toggleCollapsed(itemKey) {
                // only groupers should be collapsed / uncollapsed
                if (this.isItemGrouper(itemKey)) {
                    if (this.collapsedGroupers.includes(itemKey)) {
                        this.collapsedGroupers.splice(this.collapsedGroupers.indexOf(itemKey), 1);
                    } else {
                        this.collapsedGroupers.push(itemKey);
                    }
                }
            },
            initialMove(dragEvent) {
                for (let index = 0; index < this.$refs.filteredDisplayItems.length; index++) {
                    const displayItem = this.$refs.filteredDisplayItems[index];
                    if (displayItem.isInside(dragEvent) && !this.getItemSelected(displayItem.item.key)) {
                        this.orderable = displayItem;
                        this.dragItemKey = displayItem.item.key;
                        if (this.placeholderWidth) {
                            // overwrite placeholder width for the orderable
                            const { top, right, bottom, left, height, x, y } = displayItem.$el.getBoundingClientRect();
                            this.draggingStartRect = {
                                top,
                                right,
                                bottom,
                                left,
                                width: this.placeholderWidth,
                                height,
                                x,
                                y
                            };
                        } else {
                            this.draggingStartRect = displayItem.$el.getBoundingClientRect();
                        }

                        this._onCloneDragStart(this.dragItemKey, displayItem.$el);

                        let keys = [];
                        if (this.isItemGrouper(this.dragItemKey)) {
                            keys = [...this.internalGrouperMap[this.dragItemKey]];
                        } else {
                            keys.push(this.dragItemKey);
                        }
                        this.$emit("drag-start", { event: dragEvent, keys });
                        return;
                    }
                }
            },
            async dragMove(dragEvent) {
                if (this.orderable) {
                    this._onCloneDragMove(dragEvent);

                    await this.$nextTick();
                    this.$emit("drag-move", dragEvent);

                    this.orderableHubDragMove(dragEvent);
                }
            },
            dragEnd(dragEvent) {
                if (this.orderable) {
                    this.orderable = null;

                    this._onCloneDragEnd();
                    if (this.isItemGrouper(this.dragItemKey)) {
                        this.$emit("drag-end", { dragEvent, group: true });

                        //Make sure an already selected key is not added twice if part of the group
                        const selectedSet = new Set(this.selected);
                        const addKeys = this.internalGrouperMap[this.dragItemKey].filter((x) => !selectedSet.has(x));

                        this.orderableHubDropMultiple(addKeys);
                    } else {
                        this.$emit("drag-end", { dragEvent, group: false });
                        this.orderableHubDrop(dragEvent); // dragEvent currently unused, passing for consistency
                    }

                    this.dragItemKey = null;
                }
            },
            onFilteredItemsScroll($event) {
                if (isNearBottom($event.currentTarget)) {
                    // short debounce to collapse calls and show the loading icon
                    simpleDebounce(this._tryLoadNextFilteredItemsChunk, 250)();
                }
            },

            async _performQuery() {
                if (typeof this.items === "function") {
                    this.loadingItems = true;
                    // clone the query to make sure its not mutated
                    const queryClone = JSON.parse(JSON.stringify(this.query));
                    try {
                        const result = await this.items(queryClone);
                        this._processQueryResponse(result);
                    } catch {
                        // If query fails, alert users and give them a chance to finish their workflow.
                        await this.$app.showErrorDialog(this.queryErrorDialogTitle, this.queryErrorDialogText);
                    } finally {
                        this.loadingItems = false;
                    }
                } else {
                    // build a proxy index array to enable stable sorts.
                    const indexArray = this.items.map((_, index) => index);

                    // filter
                    const filteredIndexArray = indexArray.filter(this._matchesQuery);

                    if (this.itemGrouper) {
                        // if using groupers, restore any grouper that has items included by filters
                        this.includedKeys = new Set(
                            filteredIndexArray.map((index) => this.getItemKey(this.items[index]))
                        );
                        const restoredGroupers = indexArray.filter(this._grouperActive);
                        this.includedKeys = null;

                        restoredGroupers.forEach((grouperIndex) => filteredIndexArray.push(grouperIndex));
                    }

                    // sort
                    // this assumes that grouped items are prefixed by the group name
                    filteredIndexArray.sort(this._compareByDisplayName);

                    const reponse = {
                        Data: filteredIndexArray.map((i) => this.items[i])
                    };
                    this._processQueryResponse(reponse);
                }
            },
            _matchesQuery(index) {
                const item = this.items[index];

                // check exclusion by search term
                if (this.searchMatcher) {
                    const searchValue = this.getItemLabel(item).toLowerCase();
                    if (!this.searchMatcher.test(searchValue)) {
                        return false;
                    }
                }

                // check inclusion by filter
                if (this.activeFilters) {
                    for (const category of this.internalCategories) {
                        const categoryKey = this.getCategoryKey(category);
                        const filter = this.activeFilters.find((f) => f.Key === categoryKey);
                        if (!filter) {
                            continue;
                        }

                        const filterSet = new Set(filter.Values);
                        if (category.function && typeof category.function === "function") {
                            // invoke category exclusion function
                            if (!category.function(item, filterSet)) {
                                return false;
                            }
                        } else if (item.hasOwnProperty(category.property)) {
                            // check item property for allowed value
                            if (!filterSet.has(item[category.property])) {
                                return false;
                            }
                        }
                    }
                }

                return true;
            },
            _grouperActive(index) {
                const item = this.items[index];
                const itemKey = this.getItemKey(item);

                // exclude non-groupers
                if (!this.isItemGrouper(itemKey)) {
                    return false;
                }
                // exclude groupers already included by filters
                if (this.includedKeys.has(itemKey)) {
                    return false;
                }

                // this grouper is active if any children are active
                return this.internalGrouperMap[itemKey].some((childKey) => this.includedKeys.has(childKey));
            },
            _processQueryResponse(response) {
                // store the filtered items and immediately render the next chunk of display items
                this.filteredItems = response.Data;
                this.filteredDisplayItems = [];
                this.currentDisplayItemIndex = 0;

                if (this.$refs.itemsScrollContainer) {
                    this.$refs.itemsScrollContainer.scrollTop = 0;
                }

                this.currentDisplayItemIndex = this._populateNextFilteredItemsChunk(this.currentDisplayItemIndex);
            },
            _populateNextFilteredItemsChunk(startIndex) {
                /**
                 * For performance reasons, the filtered items are converted into display items in chunks,
                 * rather than immediately after filtering. The cost of building, rendering, and subscribing
                 * events for thousands of input components can cause hangs.
                 */
                this.loadingFilteredItemsChunk = true;
                const endIndex = Math.min(startIndex + FILTERED_ITEMS_CHUNK_SIZE, this.filteredItems.length);
                for (let index = startIndex; index < endIndex; index++) {
                    const item = this.filteredItems[index];
                    const itemKey = this.getItemKey(item);
                    const grouperKey = this.getItemGrouper(item);
                    const isGrouper = this.isItemGrouper(itemKey);

                    const displayItem = {
                        key: itemKey,
                        label: this.getItemLabel(item),
                        grouper: isGrouper,
                        grouperKey: grouperKey,
                        showIndent: Boolean(grouperKey)
                    };
                    this.filteredDisplayItems.push(displayItem);
                }
                this.loadingFilteredItemsChunk = false;
                return endIndex;
            },
            _compareByDisplayName(indexA, indexB) {
                const itemA = this.items[indexA];
                const itemB = this.items[indexB];

                const displayNameA = this.getItemLabel(itemA);
                const displayNameB = this.getItemLabel(itemB);
                return displayNameA.toString().localeCompare(displayNameB.toString());
            },
            _updateRelatedCategoriesFromSingle(category, option, selected) {
                if (!this.relatedCategoriesMap.hasOwnProperty("options")) {
                    // If no data is mapped, skip related category handling
                    return;
                }
                const categoryKey = this.getCategoryKey(category);
                const optionKey = this.getCategoryOptionKey(category, option);
                if (this.relatedCategoriesMap.options[optionKey]) {
                    // select all in child category if selecting parent
                    // deselect all in child category if deselecting parent
                    this._updateRelatedCategory(this.relatedCategoriesMap.options[optionKey], selected);
                }

                if (selected && this.relatedCategoriesMap.categories[categoryKey]) {
                    // select parent if selecting any child member
                    const parent = this.relatedCategoriesMap.categories[categoryKey];
                    if (!this.internalCategoryState[parent.category][parent.option]) {
                        this.internalCategoryState[parent.category][parent.option] = true;
                        this.$emit("filter-selected", { key: categoryKey, value: parent.optionValue, selected: true });
                    }
                }
            },
            _updateRelatedCategoriesFromAll(category, selected) {
                if (!this.relatedCategoriesMap.hasOwnProperty("options")) {
                    // If no data is mapped, skip related category handling
                    return;
                }
                const categoryKey = this.getCategoryKey(category);
                if (this.relatedCategoriesMap.categories[categoryKey]) {
                    const parent = this.relatedCategoriesMap.categories[categoryKey];
                    if (
                        (selected && !this.internalCategoryState[parent.category][parent.option]) ||
                        (!selected && this.internalCategoryState[parent.category][parent.option])
                    ) {
                        // select parent option if selecting all for child category
                        // deselect parent option if deselecting all for child category
                        this.internalCategoryState[parent.category][parent.option] = selected;
                        this.$emit("filter-selected", { key: categoryKey, value: parent.optionValue, selected });
                    }
                }

                // select/deselect all children if a "select all/none" action removes a parent
                category.options.forEach((option) => {
                    const optionKey = this.getCategoryOptionKey(category, option);
                    if (this.relatedCategoriesMap.options[optionKey]) {
                        // deselect all in child category if deselecting parent
                        this._updateRelatedCategory(this.relatedCategoriesMap.options[optionKey], selected);
                    }
                });
            },
            _updateRelatedCategory(categoryKey, selected) {
                const childCategory = this.internalCategories.find(
                    (category) => this.getCategoryKey(category) === categoryKey
                );
                if (childCategory) {
                    const childCategoryKey = this.getCategoryKey(childCategory);
                    childCategory.options.forEach((childOption) => {
                        const childOptionKey = this.getCategoryOptionKey(childCategory, childOption);
                        if (this.getCategoryOptionEnabled(childOption)) {
                            this.internalCategoryState[childCategoryKey][childOptionKey] = selected;
                            this.$emit("filter-selected", {
                                key: childCategoryKey,
                                value: childOption.value,
                                selected
                            });
                        }
                    });
                }
            },
            _buildCategoryState(existingState, useInitial) {
                // need to rebuild state to match new source categories
                const categoryState = {};
                if (!existingState) {
                    existingState = {};
                }

                this.categories.forEach((category) => {
                    const categoryKey = this.getCategoryKey(category);
                    // if a category is removed from the property, its state is lost
                    categoryState[categoryKey] = {};
                    if (category.options) {
                        category.options.forEach((option) => {
                            const optionKey = this.getCategoryOptionKey(category, option);

                            //use initial is needed b/c on resetFilter this method is called before the prop update from
                            //this.$emit("filter-reset") reaches this component
                            if (useInitial) {
                                categoryState[categoryKey][optionKey] = option.initiallySelected ?? false;
                            } else if (!this.getCategoryOptionEnabled(option)) {
                                categoryState[categoryKey][optionKey] = false;
                            } else if (
                                existingState.hasOwnProperty(categoryKey) &&
                                existingState[categoryKey].hasOwnProperty(optionKey)
                            ) {
                                // maintain any existing state
                                categoryState[categoryKey][optionKey] = existingState[categoryKey][optionKey];
                            } else {
                                // use option default
                                categoryState[categoryKey][optionKey] = false;
                            }
                        });
                    }
                });
                return categoryState;
            },
            async _buildInternalGrouperMap() {
                this.internalGrouperMap = {};
                // if not configured, skip attempting to find groupers
                if (!this.itemGrouper) {
                    return;
                }

                if (typeof this.items === "function") {
                    // invoke function with unfiltered query, use full result list to build grouper map
                    this.loadingGroupers = true;
                    const emptyQuery = {
                        Search: {
                            Value: "",
                            IsRegex: false
                        },
                        CategorizedFilters: []
                    };
                    try {
                        const result = await this.items(emptyQuery);
                        this._fillInternalGrouperMap(result.Data);
                    } finally {
                        this.loadingGroupers = false;
                    }
                } else {
                    this._fillInternalGrouperMap(this.items);
                }
            },
            _fillInternalGrouperMap(unfilteredItems) {
                unfilteredItems.forEach((item) => {
                    const groupKey = this.getItemGrouper(item);
                    if (groupKey) {
                        if (!this.internalGrouperMap.hasOwnProperty(groupKey)) {
                            this.internalGrouperMap[groupKey] = [];
                        }
                        this.internalGrouperMap[groupKey].push(this.getItemKey(item));
                    }
                });
            },
            _onCloneDragStart(key, itemElement) {
                const parentRect = this.$refs.itemsWrapper.getBoundingClientRect();
                const rect = itemElement.getBoundingClientRect();
                const style = window.getComputedStyle(itemElement);
                const leftMargin = this._stringPxToFloat(style.marginLeft);
                const topMargin = this._stringPxToFloat(style.marginTop);
                this.dragState.left0 = rect.left - parentRect.left - leftMargin;
                this.dragState.top0 = rect.top - parentRect.top - topMargin;
                this.dragState.x = this.dragState.left0;
                this.dragState.y = this.dragState.top0;
                this.dragState.rect = rect;
                this.dragState.parentRect = parentRect;
                this.dragState.key = key;
                this.dragState.moving = true;
                window.requestAnimationFrame(this._calcDragMove);
            },
            _onCloneDragMove(dragEvent) {
                if (this.dragState.moving) {
                    this.dragState.event = dragEvent;
                }
            },
            _onCloneDragEnd() {
                this.dragState.x = 0;
                this.dragState.y = 0;
                this.dragState.key = null;
                this.dragState.moving = false;
            },
            _calcDragMove() {
                const event = this.dragState.event;
                if (event && !event.processed) {
                    event.processed = true;
                    const parentRect = this.$refs.itemsWrapper.getBoundingClientRect();
                    this.dragState.x =
                        this.dragState.left0 +
                        (event.clientX - event.clientX0) -
                        (parentRect.left - this.dragState.parentRect.left);
                    this.dragState.y =
                        this.dragState.top0 +
                        (event.clientY - event.clientY0) -
                        (parentRect.top - this.dragState.parentRect.top);
                }
                if (this.dragState.moving) {
                    window.requestAnimationFrame(this._calcDragMove);
                }
            },
            async _tryLoadNextFilteredItemsChunk() {
                if (this.loadingFilteredItemsChunk || !this.moreFilteredItemsToDisplay) {
                    return;
                }

                this.currentDisplayItemIndex = this._populateNextFilteredItemsChunk(this.currentDisplayItemIndex);
            },
            _stringPxToFloat(px) {
                const res = parseFloat(px);
                return isNaN(res) ? 0 : res;
            }
        }
    };
</script>

<style lang="scss" scoped>
    .list-item {
        position: relative;
        transition-property: transform;
        transition-duration: 0.4s;
    }
    .list-enter-from,
    .list-leave-to,
    .list-leave-from {
        opacity: 0;
    }
    .list-leave-active {
        position: absolute;
        transition-duration: 0s;
    }
</style>
